import {
    PDFArray,
    PDFDict,
    PDFName,
    PDFNumber,
    PDFRef,
    PDFStream,
    type PDFContext,
    type PDFDocument,
    type PDFObject,
} from "pdf-lib";

/**
 * Aufräumen nach dem Entfernen von Seiten.
 *
 * `baueDokument` baut im Hauptdokument um (Formular, Metadaten bleiben). pdf-lib
 * nimmt entfernte Seiten dabei nur aus dem Seitenbaum — Seitenobjekt, Inhalt
 * und Bilder bleiben in der Datei. Das ist ein Datenschutzproblem (die
 * „gelöschte“ Seite lässt sich mit jedem PDF-Werkzeug zurückholen) und macht
 * die Datei nicht kleiner. Darum:
 *
 * 1. Formularfelder, deren Widgets auf keiner verbleibenden Seite hängen,
 *    aus dem AcroForm nehmen.
 * 2. Lesezeichen, die auf eine entfernte Seite zeigen, entfernen (mit
 *    Unterpunkten: nur das Ziel streichen, die Unterpunkte bleiben).
 * 3. Alles, was vom Trailer aus nicht mehr erreichbar ist, löschen. Entfernte
 *    Seiten gelten dabei als Sackgasse: Verweise darauf (Sprungziele in Links,
 *    benannte Ziele, Struktur-Baum, OpenAction) zeigen danach ins Leere, was
 *    PDF-Leser laut Spezifikation als `null` lesen — der Sprung tut nichts.
 */
export function raeumeAuf(doc: PDFDocument, entfernt: ReadonlySet<PDFRef>): void {
    if (entfernt.size === 0) return;
    bereinigeFormular(doc);
    bereinigeLesezeichen(doc, entfernt);
    sammleMuell(doc.context, entfernt);
}

function bereinigeFormular(doc: PDFDocument): void {
    const ctx = doc.context;
    const acroForm = doc.catalog.lookupMaybe(PDFName.of("AcroForm"), PDFDict);
    const felder = acroForm?.lookupMaybe(PDFName.of("Fields"), PDFArray);
    if (!acroForm || !felder) return;

    const seiten = new Set<PDFRef>();
    const widgets = new Set<PDFObject>();
    for (const seite of doc.getPages()) {
        seiten.add(seite.ref);
        const annots = seite.node.Annots();
        if (annots) for (const a of annots.asArray()) widgets.add(a);
    }
    // Widget gilt als lebendig, wenn eine verbleibende Seite es in /Annots
    // führt — oder, bei schlampig geschriebenen PDFs ohne /Annots-Eintrag,
    // wenn sein /P auf eine verbleibende Seite zeigt.
    const lebt = (ref: PDFRef, dict: PDFDict) => {
        const p = dict.get(PDFName.of("P"));
        return widgets.has(ref) || (p instanceof PDFRef && seiten.has(p));
    };

    const gesehen = new Set<PDFRef>();
    const behalte = (obj: PDFObject): boolean => {
        if (!(obj instanceof PDFRef)) return true; // direktes Objekt: nicht zuordenbar, bleibt
        if (gesehen.has(obj)) return false; // Zyklus
        gesehen.add(obj);
        const dict = ctx.lookupMaybe(obj, PDFDict);
        if (!dict) return false;
        const kids = dict.lookupMaybe(PDFName.of("Kids"), PDFArray);
        if (kids) {
            const rest = kids.asArray().filter(behalte);
            if (rest.length > 0) {
                dict.set(PDFName.of("Kids"), ctx.obj(rest));
                return true;
            }
        }
        return lebt(obj, dict);
    };
    acroForm.set(PDFName.of("Fields"), ctx.obj(felder.asArray().filter(behalte)));
}

function bereinigeLesezeichen(doc: PDFDocument, entfernt: ReadonlySet<PDFRef>): void {
    const ctx = doc.context;
    const wurzel = doc.catalog.lookupMaybe(PDFName.of("Outlines"), PDFDict);
    if (!wurzel) return;
    const N = (n: string) => PDFName.of(n);

    /** Zeigt das Lesezeichen per Seitenverweis auf eine entfernte Seite? */
    const istTot = (item: PDFDict): boolean => {
        let ziel: PDFObject | undefined = item.get(N("Dest"));
        if (ziel === undefined) {
            const aktion = item.lookupMaybe(N("A"), PDFDict);
            if (aktion && aktion.get(N("S")) === N("GoTo")) ziel = aktion.get(N("D"));
        }
        // Benannte Ziele (Name/String) werden hier nicht aufgelöst — sie zeigen
        // nach dem Aufräumen ins Leere und tun dann nichts.
        const arr = ziel === undefined ? undefined : ctx.lookup(ziel);
        const seite = arr instanceof PDFArray ? arr.get(0) : undefined;
        return seite instanceof PDFRef && entfernt.has(seite);
    };

    const gesehen = new Set<PDFRef>();
    /** Bereinigt die Kinder von `eltern`; liefert die Zahl sichtbarer Nachkommen (aufgeklappt gedacht). */
    const bereinige = (eltern: PDFDict): number => {
        const kinder: PDFRef[] = [];
        let r = eltern.get(N("First"));
        while (r instanceof PDFRef && !gesehen.has(r)) {
            gesehen.add(r);
            kinder.push(r);
            r = ctx.lookupMaybe(r, PDFDict)?.get(N("Next"));
        }
        const behalten: PDFRef[] = [];
        let sichtbar = 0;
        for (const k of kinder) {
            const item = ctx.lookupMaybe(k, PDFDict);
            if (!item) continue;
            const unter = bereinige(item);
            if (istTot(item)) {
                if (item.get(N("First")) === undefined) continue;
                // Mit Unterpunkten bleibt der Eintrag als Überschrift stehen.
                item.delete(N("Dest"));
                item.delete(N("A"));
            }
            behalten.push(k);
            const count = item.lookupMaybe(N("Count"), PDFNumber)?.asNumber() ?? 0;
            sichtbar += 1 + (count > 0 ? unter : 0);
        }
        behalten.forEach((k, i) => {
            const item = ctx.lookup(k, PDFDict);
            if (i > 0) item.set(N("Prev"), behalten[i - 1]);
            else item.delete(N("Prev"));
            if (i < behalten.length - 1) item.set(N("Next"), behalten[i + 1]);
            else item.delete(N("Next"));
        });
        if (behalten.length === 0) {
            eltern.delete(N("First"));
            eltern.delete(N("Last"));
            eltern.delete(N("Count"));
            return 0;
        }
        eltern.set(N("First"), behalten[0]);
        eltern.set(N("Last"), behalten[behalten.length - 1]);
        // Vorzeichen erhalten: negativ = zugeklappt.
        const alt = eltern.lookupMaybe(N("Count"), PDFNumber)?.asNumber();
        if (alt !== undefined) eltern.set(N("Count"), PDFNumber.of(alt < 0 ? -sichtbar : sichtbar));
        return sichtbar;
    };

    bereinige(wurzel);
    if (wurzel.get(N("First")) === undefined) doc.catalog.delete(N("Outlines"));
}

/** Löscht alle indirekten Objekte, die vom Trailer aus nicht erreichbar sind. */
function sammleMuell(ctx: PDFContext, entfernt: ReadonlySet<PDFRef>): void {
    const erreicht = new Set<PDFRef>();
    const stapel: PDFObject[] = Object.values(ctx.trailerInfo).filter((x): x is PDFObject => x !== undefined);
    while (stapel.length > 0) {
        const o = stapel.pop();
        if (o instanceof PDFRef) {
            if (erreicht.has(o) || entfernt.has(o)) continue;
            erreicht.add(o);
            const ziel = ctx.lookup(o);
            if (ziel !== undefined) stapel.push(ziel);
        } else if (o instanceof PDFDict) {
            for (const [, v] of o.entries()) stapel.push(v);
        } else if (o instanceof PDFArray) {
            stapel.push(...o.asArray());
        } else if (o instanceof PDFStream) {
            stapel.push(o.dict);
        }
    }
    for (const [ref] of ctx.enumerateIndirectObjects()) {
        if (!erreicht.has(ref)) ctx.delete(ref);
    }
}
